/**
 * Примитивы графиков подвкладки «Посещаемость» (DC → Юнифанс, 21.09, владелец).
 * Рисуем сами: библиотек графиков в проекте нет, а инлайновый SVG на токенах
 * ложится в тёмную тему и не тянет зависимостей.
 *
 * Правила, по которым сделано: одна ось (без второй шкалы), ось значений всегда от
 * нуля, тонкие марки и просветы 2px между сегментами стопки, легенда при ≥2 сериях,
 * прямые подписи вместо цифр на каждой точке, ховер с тултипом, серии красятся по
 * сущности (канал/экран), а не по месту в рейтинге — цвет не «переезжает» при фильтре.
 */
import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";

/** Палитра серий — токены --chart-1..6 (проверены валидатором на тёмной подложке). */
export const SERIES_COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)", "var(--chart-6)"];
export const NUM: CSSProperties = { fontFamily: "var(--f-meta)", fontVariantNumeric: "tabular-nums" };
export const fmtNum = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

/** Ширина контейнера: SVG рисуем в реальных пикселях — текст не «плывёт» от масштабирования. */
export function useWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [w, setW] = useState(320);
  const ref = useRef<HTMLDivElement | null>(null);
  const set = (el: HTMLDivElement | null) => { ref.current = el; if (el) setW(el.clientWidth || 320); };
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setW(el.clientWidth || 320));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [set, w];
}

export interface Serie { key: string; label: string; color: string }
export interface Point { x: string; label: string; values: Record<string, number> }

const AX = { left: 30, right: 8, top: 10, bottom: 18 };
const gridStroke = "var(--surface-3)";

/** Шкала «снизу вверх» с округлением верха до «красивого» числа. */
function niceMax(v: number): number {
  /* Верх кратен двум: середина сетки подписывается целым числом, а не «3» вместо 2,5. */
  if (v <= 4) return Math.max(2, Math.ceil(v / 2) * 2);
  const p = 10 ** Math.floor(Math.log10(v));
  const m = Math.ceil(v / (p / 2)) * (p / 2);
  return (m / 2) % 1 === 0 ? m : m + p / 2;
}

/**
 * Бары по времени: одна серия — сплошные, несколько — стопка с просветом 2px.
 * Тап/наведение по колонке даёт тултип со всеми сериями этой точки.
 */
export function BarsChart(
  { points, series, height = 132, onPick, partialX }:
  { points: Point[]; series: Serie[]; height?: number; onPick?: (p: Point | null) => void; partialX?: string },
): ReactElement {
  const [setRef, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const innerW = Math.max(120, w - AX.left - AX.right);
  const innerH = height - AX.top - AX.bottom;
  const totals = points.map((p) => series.reduce((s, x) => s + (p.values[x.key] ?? 0), 0));
  const max = niceMax(Math.max(1, ...totals));
  const step = innerW / Math.max(1, points.length);
  const bw = Math.max(2, Math.min(28, step - (step > 8 ? 3 : 1)));
  const ticks = [0, max / 2, max];
  /* Значения пишем прямо на барах, пока их мало: на тач-экране ховера нет вовсе. */
  const labelVals = series.length === 1 && points.length <= 14;
  /* Подписей по X ровно столько, сколько влезает без наложения. */
  const every = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(innerW / 44))));

  return (
    <div ref={setRef} style={{ position: "relative", width: "100%" }}>
      <svg width={w} height={height} role="img" aria-label="График по времени" style={{ display: "block", touchAction: "pan-y" }}
        onPointerLeave={() => { setHover(null); onPick?.(null); }}>
        {ticks.map((t) => {
          const y = AX.top + innerH - (t / max) * innerH;
          return (
            <g key={t}>
              <line x1={AX.left} x2={w - AX.right} y1={y} y2={y} stroke={gridStroke} strokeWidth={1} />
              <text x={AX.left - 4} y={y + 3} textAnchor="end" fontSize={9} fill="var(--text-3)" style={NUM}>{fmtNum(t)}</text>
            </g>
          );
        })}
        {points.map((p, i) => {
          const x = AX.left + i * step + (step - bw) / 2;
          let acc = 0;
          return (
            <g key={p.x} onPointerEnter={() => { setHover(i); onPick?.(p); }} onPointerDown={() => { setHover(i); onPick?.(p); }}>
              <rect x={AX.left + i * step} y={AX.top} width={step} height={innerH} fill={hover === i ? "var(--surface-2)" : "transparent"} />
              {series.map((s) => {
                const v = p.values[s.key] ?? 0;
                if (v <= 0) return null;
                const h = (v / max) * innerH;
                const y = AX.top + innerH - acc - h;
                acc += h + (series.length > 1 ? 2 : 0);   // просвет 2px между сегментами
                return <rect key={s.key} x={x} y={y} width={bw} height={Math.max(1, h)} rx={bw > 6 ? 2 : 0} fill={s.color} />;
              })}
              {labelVals && (p.values[series[0]!.key] ?? 0) > 0 ? (
                <text x={AX.left + i * step + step / 2} y={AX.top + innerH - ((p.values[series[0]!.key] ?? 0) / max) * innerH - 3}
                  textAnchor="middle" fontSize={9} fill="var(--text-2)" style={NUM}>{fmtNum(p.values[series[0]!.key] ?? 0)}</text>
              ) : null}
              {p.x === partialX ? (   /* сегодняшний день ещё идёт — не «падение» */
                <text x={AX.left + i * step + step / 2} y={AX.top + 8} textAnchor="middle" fontSize={8} fill="var(--text-3)">идёт</text>
              ) : null}
              {i % every === 0 ? (
                <text x={AX.left + i * step + step / 2} y={height - 5} textAnchor="middle" fontSize={9} fill="var(--text-3)" style={NUM}>{p.label}</text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Линия с заливкой — для длинных периодов (одна серия, точек больше, чем баров влезает). */
export function LineChart(
  { points, series, height = 132, onPick }:
  { points: Point[]; series: Serie; height?: number; onPick?: (p: Point | null) => void },
): ReactElement {
  const [setRef, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const innerW = Math.max(120, w - AX.left - AX.right);
  const innerH = height - AX.top - AX.bottom;
  const vals = points.map((p) => p.values[series.key] ?? 0);
  const max = niceMax(Math.max(1, ...vals));
  const stepX = innerW / Math.max(1, points.length - 1);
  const xy = (i: number, v: number): [number, number] => [AX.left + i * stepX, AX.top + innerH - (v / max) * innerH];
  const line = vals.map((v, i) => { const [x, y] = xy(i, v); return `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`; }).join(" ");
  const area = `${line} L${(AX.left + (points.length - 1) * stepX).toFixed(1)},${AX.top + innerH} L${AX.left},${AX.top + innerH} Z`;
  const every = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(innerW / 48))));
  const lastI = points.length - 1;

  return (
    <div ref={setRef} style={{ position: "relative", width: "100%" }}>
      <svg width={w} height={height} role="img" aria-label="График по времени" style={{ display: "block", touchAction: "pan-y" }}
        onPointerLeave={() => { setHover(null); onPick?.(null); }}
        onPointerMove={(e) => {
          const box = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const i = Math.round((e.clientX - box.left - AX.left) / stepX);
          const k = Math.max(0, Math.min(points.length - 1, i));
          setHover(k); onPick?.(points[k] ?? null);
        }}>
        {[0, max / 2, max].map((t) => {
          const y = AX.top + innerH - (t / max) * innerH;
          return (
            <g key={t}>
              <line x1={AX.left} x2={w - AX.right} y1={y} y2={y} stroke={gridStroke} strokeWidth={1} />
              <text x={AX.left - 4} y={y + 3} textAnchor="end" fontSize={9} fill="var(--text-3)" style={NUM}>{fmtNum(t)}</text>
            </g>
          );
        })}
        <path d={area} fill={series.color} opacity={0.18} />
        <path d={line} fill="none" stroke={series.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {hover != null && points[hover] ? (() => {
          const [x, y] = xy(hover, vals[hover] ?? 0);
          return (<g><line x1={x} x2={x} y1={AX.top} y2={AX.top + innerH} stroke={gridStroke} strokeWidth={1} />
            <circle cx={x} cy={y} r={4} fill="var(--surface-1)" stroke={series.color} strokeWidth={2} /></g>);
        })() : null}
        {points.map((p, i) => (i % every === 0 || i === lastI ? (
          <text key={p.x} x={AX.left + i * stepX} y={height - 5} textAnchor={i === lastI ? "end" : "middle"} fontSize={9} fill="var(--text-3)" style={NUM}>{p.label}</text>
        ) : null))}
      </svg>
    </div>
  );
}

/** Горизонтальные бары с прямыми подписями — «где люди проводят время». */
export function HBars({ rows, color = "var(--chart-1)", unit }: { rows: { label: string; value: number; sub?: string }[]; color?: string; unit?: string }): ReactElement {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: "grid", gridTemplateColumns: "96px 1fr auto", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.label}>{r.label}</span>
          <span style={{ position: "relative", height: 12, background: "var(--surface-2)", borderRadius: 2, overflow: "hidden" }}>
            <span style={{ position: "absolute", insetBlock: 0, left: 0, width: `${(r.value / max) * 100}%`, background: color, borderRadius: 2 }} />
          </span>
          <span style={{ ...NUM, fontSize: 11, color: "var(--text-1)", minWidth: 62, textAlign: "right" }}>
            {fmtNum(r.value)}{unit ? <span style={{ color: "var(--text-3)" }}> {unit}</span> : null}
            {r.sub ? <span style={{ color: "var(--text-3)", fontSize: 9.5 }}> · {r.sub}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Тепловая карта «день недели × час»: когда люди заходят. Шкала последовательная —
 * один тон от прозрачного к плотному, 5 ступеней; ноль — цвет подложки, а не «светлая
 * ступень», иначе пустая ночь читается как активность.
 */
export function Heatmap(
  { cells, shade, rowLabels, colLabels, max, onPick }:
  { cells: number[][]; shade?: number[][]; rowLabels: string[]; colLabels: string[]; max: number; onPick?: (t: string | null) => void },
): ReactElement {
  /* cells — что показываем в подсказке (люди), shade — чем красим (на один такой день недели). */
  const tone = shade ?? cells;
  const level = (v: number) => (v <= 0 ? 0 : Math.min(5, Math.ceil((v / Math.max(1, max)) * 5)));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "26px 1fr", gap: 3, alignItems: "center" }}>
      <span />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${colLabels.length}, 1fr)`, gap: 1 }}>
        {colLabels.map((l, i) => (
          <span key={l} style={{ ...NUM, fontSize: 7.5, color: "var(--text-3)", textAlign: "center" }}>{i % 3 === 0 ? l : ""}</span>
        ))}
      </div>
      {cells.map((row, r) => (
        <Fragment key={rowLabels[r]}>
          <span style={{ ...NUM, fontSize: 9, color: "var(--text-3)" }}>{rowLabels[r]}</span>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${row.length}, 1fr)`, gap: 1 }}>
            {row.map((v, i) => (
              <span key={i} title={`${rowLabels[r]} ${colLabels[i]}:00 — ${fmtNum(v)}`}
                onPointerEnter={() => onPick?.(v > 0 ? `${rowLabels[r]}, ${colLabels[i]}:00 — ${fmtNum(v)}` : null)}
                onPointerDown={() => onPick?.(v > 0 ? `${rowLabels[r]}, ${colLabels[i]}:00 — ${fmtNum(v)}` : null)}
                style={{
                  height: 14, borderRadius: 2,
                  background: v <= 0 ? "var(--surface-2)" : `color-mix(in srgb, var(--chart-1) ${level(tone[r]?.[i] ?? v) * 20}%, var(--surface-2))`,
                }} />
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/** Легенда: идентичность серии — не только цветом, но и подписью рядом. */
export function Legend({ series }: { series: Serie[] }): ReactElement {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
      {series.map((s) => (
        <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "var(--text-2)" }}>
          <i style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} aria-hidden="true" />{s.label}
        </span>
      ))}
    </div>
  );
}
